/**
 * MonadicThemeHeatmap — sentiment-grouped heatmap of open-end themes across assets.
 *
 * Rows = themes, grouped into Positive / Mixed / Negative sections (Other themes render
 * last, chip-free, ungrouped — mirrors the QuickStudyOpenEndCard convention). Columns =
 * assets (pages/messages/ads). Each cell tints on a SEQUENTIAL ramp (never diverging —
 * a theme's mention share has no "bad direction" the way a rating scale does) off the
 * shared QS diverging palette: a Positive-group row ramps teal, a Negative-group row
 * ramps coral, a Mixed/Other row ramps the neutral gray band. The printed percent is
 * ALWAYS a plain text token — it never wears the fill color, matching the ScorecardHeatmap
 * convention this exhibit reuses chrome from (see DivergingStackedRating.tsx `renderTable`,
 * ~lines 839-868).
 *
 * Render-only: no state, no fetching. The caller owns column derivation (via the exported
 * `deriveThemeColumns`) so a gate upstream can decide whether the heatmap renders at all
 * before this component ever mounts.
 */
import { SC_NEGATIVE, SC_NEUTRAL_BAND, SC_POSITIVE, sequentialCellTone } from './DivergingStackedRating'
import { groupThemesBySentiment, themeDisplayName } from './openEndThemeGroups'
import { useThemeOtherLabel } from './useThemeOtherLabel'
import { SentimentGroupChip } from './SentimentChips'
import { ComparisonLabelText } from './ComparisonLabelText'
import { stripLabelQuotes } from './quickStudyPure'

interface HeatmapVariant {
  creative_id: string
  label: string
  count: number
  percent: number
}

interface HeatmapTheme {
  theme: string
  is_other?: boolean
  description?: string
  sentiment?: string
  variants: Array<HeatmapVariant>
}

export interface ThemeColumn {
  id: string
  label: string
  /** Combo band-heatmap columns (Step G): band definition + n ("scores 1-2 of 5 · n=42"),
   *  shown as the header cell's tooltip. Absent on monadic callers → falls back to `label`. */
  title?: string
}

/** Columns = perItem (authoritative order + server-minted labels) when non-empty, else the
 *  ordered first-seen union of variants across all themes (the combo Drivers consumer passes
 *  perItem=[]). Pure. The `themes` parameter is deliberately structural-minimal (only the
 *  variant identity fields are read), so wire-shaped callers need no cast. */
export interface ThemeColumnSource {
  variants?: Array<{ creative_id: string; label: string }>
}

export function deriveThemeColumns(
  perItem: Array<{ creative_id: string; label: string }>,
  themes: Array<ThemeColumnSource>,
): Array<ThemeColumn> {
  if (perItem.length > 0) {
    return perItem.map((p) => ({ id: p.creative_id, label: p.label }))
  }
  const seen = new Set<string>()
  const out: Array<ThemeColumn> = []
  for (const t of themes) {
    for (const v of t.variants ?? []) {
      if (!seen.has(v.creative_id)) {
        seen.add(v.creative_id)
        out.push({ id: v.creative_id, label: v.label })
      }
    }
  }
  return out
}

type ThemePolarity = 'positive' | 'mixed' | 'negative' | 'neutral'

function poleFor(polarity: ThemePolarity): string {
  if (polarity === 'positive') return SC_POSITIVE
  if (polarity === 'negative') return SC_NEGATIVE
  return SC_NEUTRAL_BAND
}

interface ThemeRowProps {
  theme: HeatmapTheme
  columns: Array<ThemeColumn>
  polarity: ThemePolarity
  comparisonLabels: Array<string>
}

function ThemeRow({ theme, columns, polarity, comparisonLabels }: ThemeRowProps) {
  const pole = poleFor(polarity)
  const otherLabel = useThemeOtherLabel()
  return (
    <tr data-testid="qs-monadic-theme-heatmap-row">
      <th
        scope="row"
        className="w-full truncate pr-2 text-left align-middle font-normal text-twin-chat-fg"
        title={stripLabelQuotes(theme.description || theme.theme)}
      >
        <ComparisonLabelText text={stripLabelQuotes(themeDisplayName(theme, otherLabel))} labels={comparisonLabels} />
      </th>
      {columns.map((col) => {
        const v = (theme.variants ?? []).find((x) => x.creative_id === col.id)
        const percent = v?.percent ?? 0
        const count = v?.count ?? 0
        const tone = sequentialCellTone(percent, pole)
        return (
          <td
            key={col.id}
            data-testid="qs-monadic-theme-heatmap-cell"
            data-polarity={polarity}
            data-intensity={tone.intensity}
            className="rounded-md px-2 py-1.5 text-center align-middle tabular-nums text-twin-chat-fg"
            style={{ background: tone.background }}
            title={`${Math.round(percent)}% · ${count}`}
          >
            {Math.round(percent)}%
          </td>
        )
      })}
    </tr>
  )
}

interface MonadicThemeHeatmapProps {
  themes: Array<HeatmapTheme>
  columns: Array<ThemeColumn>
  comparisonLabels?: Array<string>
}

export function MonadicThemeHeatmap({
  themes,
  columns,
  comparisonLabels = [],
}: MonadicThemeHeatmapProps) {
  const { groups, other } = groupThemesBySentiment(themes)

  return (
    <div data-testid="qs-monadic-theme-heatmap">
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-2 text-xs" role="table">
          <thead>
            <tr>
              <th scope="col" className="w-full text-left align-bottom font-medium text-twin-chat-fg-muted">
                Theme
              </th>
              {columns.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  className="w-64 truncate px-1 text-center align-bottom font-medium text-twin-chat-fg"
                  title={stripLabelQuotes(col.title ?? col.label)}
                >
                  <ComparisonLabelText text={stripLabelQuotes(col.label)} labels={comparisonLabels} />
                </th>
              ))}
            </tr>
          </thead>
          {/* One <tbody> per sentiment section, its heading a `scope="rowgroup"` <th> — so the
              Positive/Mixed/Negative grouping is structural for assistive tech, not a visual-only
              header cell (a `scope="colgroup"` here would misassociate the label with columns). */}
          {groups.map((g) => (
            <tbody key={g.group}>
              <tr data-testid="qs-monadic-theme-heatmap-group" data-group={g.group}>
                <th colSpan={columns.length + 1} scope="rowgroup" className="pt-2 text-left">
                  <SentimentGroupChip group={g.group} />
                </th>
              </tr>
              {g.themes.map((theme) => (
                <ThemeRow
                  key={`${g.group}-${theme.theme}`}
                  theme={theme}
                  columns={columns}
                  polarity={g.group}
                  comparisonLabels={comparisonLabels}
                />
              ))}
            </tbody>
          ))}
          {other.length > 0 && (
            <tbody>
              {other.map((theme) => (
                <ThemeRow
                  key={`other-${theme.theme}`}
                  theme={theme}
                  columns={columns}
                  polarity="neutral"
                  comparisonLabels={comparisonLabels}
                />
              ))}
            </tbody>
          )}
        </table>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-twin-chat-fg-muted">
        <span
          aria-hidden
          className="h-2.5 w-10 rounded-sm"
          style={{ background: `linear-gradient(to right, ${SC_NEUTRAL_BAND}, ${SC_POSITIVE})` }}
        />
        <span>Positive</span>
        <span
          aria-hidden
          className="h-2.5 w-10 rounded-sm"
          style={{ background: `linear-gradient(to right, ${SC_NEUTRAL_BAND}, ${SC_NEGATIVE})` }}
        />
        <span>Negative</span>
        <span>darker = more twins mentioned it</span>
      </div>
    </div>
  )
}
